// src/components/desk/keywordEditors.jsx — the EDITOR REGISTRY for search-bar keywords.
//
// Owner ruling 2026-07-24, before a line of this was written: a chip DESCRIBES a constraint and
// must not know how to edit itself. The moment chips carry options, ranges, enums, steppers and
// popover config, chip generation quietly becomes a form-definition system, and every new facet
// then means editing three places at once — generation, rendering, editing.
//
// So the split is:
//   • a chip exposes a stable identifier    → { key, label, editKey: 'price' }
//   • this registry says how that is edited → KEYWORD_EDITORS['price']
//   • <KeywordEditor> renders it against the ONE filter model (advFilters)
//
// Adding a facet is ONE DATA ENTRY below. If you ever find yourself adding a `kind` here to
// serve a single facet, that facet probably wants a plain control instead — the point of the
// registry is that editors are shared shapes, not per-field bespoke UI.
//
// ⚠️ Editors mutate the SAME advFilters the FacetRail writes. They must never write entrySearch:
// keywords projected from the model are not text, and generating text from filters is the
// reverse-compile trap that invariant 10a exists to forbid (see `search-bar-keyword-projection`).
import { t } from '../../lib/i18n.js'
import { pill } from './primitives.jsx'
import { addDistrictIncludes } from '../../lib/districtFilter.js'

// ── the registry: editKey → how it is edited ────────────────────────────────
// `kind` picks the shared editor shape; the rest is that shape's parameters.
export const KEYWORD_EDITORS = {
  price:        { kind: 'range', min: 'priceMin',       max: 'priceMax' },
  ppm:          { kind: 'range', min: 'ppmMin',         max: 'ppmMax' },
  area:         { kind: 'range', min: 'areaMin',        max: 'areaMax' },
  landArea:     { kind: 'range', min: 'landAreaMin',    max: 'landAreaMax' },
  bathrooms:    { kind: 'range', min: 'bathroomsMin',   max: 'bathroomsMax' },
  balcony_count:{ kind: 'range', min: 'balconyCountMin', max: 'balconyCountMax' },   // 0757
  floor:        { kind: 'range', min: 'floorMin',       max: 'floorMax' },
  floorsTotal:  { kind: 'range', min: 'floorsTotalMin', max: 'floorsTotalMax' },
  roomsSet:     { kind: 'set',   field: 'roomsSet',     values: [1, 2, 3, 4, 5] },
  bedroomsSet:  { kind: 'set',   field: 'bedroomsSet',  values: [1, 2, 3, 4, 5] },
  intents:      { kind: 'options', field: 'intents' },
  kinds:        { kind: 'options', field: 'kinds' },
  districtCodes:{ kind: 'districts' },
  topFloor:     { kind: 'flag',  field: 'topFloor' },
}

const numOrBlank = v => (v === '' || v == null ? '' : v)

// ── shared editor shapes ────────────────────────────────────────────────────
function RangeEditor({ spec, filters, setFilters }) {
  const set = (k, v) => setFilters(f => ({ ...f, [k]: v === '' ? '' : Number(v) }))
  return (
    <span className="flex items-center gap-1">
      <input type="number" inputMode="numeric" value={numOrBlank(filters[spec.min])}
        onChange={e => set(spec.min, e.target.value)} placeholder={t('desk_range_from_ph')}
        className="w-20 rounded border border-border bg-background px-1.5 py-0.5 text-xs" />
      <span className="text-muted-foreground text-xs">–</span>
      <input type="number" inputMode="numeric" value={numOrBlank(filters[spec.max])}
        onChange={e => set(spec.max, e.target.value)} placeholder={t('desk_range_to_ph')}
        className="w-20 rounded border border-border bg-background px-1.5 py-0.5 text-xs" />
    </span>
  )
}

// Multi-select over a fixed numeric ladder (rooms/bedrooms). 5 means "5+", exactly as the facet
// rail draws it — the label comes from the caller so the two can never disagree.
function SetEditor({ spec, filters, setFilters }) {
  const cur = filters[spec.field] || []
  const toggle = n => setFilters(f => {
    const s = new Set(f[spec.field] || [])
    s.has(n) ? s.delete(n) : s.add(n)
    return { ...f, [spec.field]: [...s].sort((a, b) => a - b) }
  })
  return (
    <span className="flex items-center gap-1">
      {spec.values.map(n => (
        <button key={n} type="button" onClick={() => toggle(n)}
          className={pill('none', { door: true, extra: cur.includes(n)
            ? 'border-primary bg-primary/15 text-foreground' : 'border-border text-muted-foreground hover:bg-muted' })}>
          {n === 5 ? '5+' : n}
        </button>
      ))}
    </span>
  )
}

// Multi-select over a vocabulary the CALLER supplies (intents, property kinds). The registry
// deliberately does not carry the option lists: they live where they are already defined, and
// duplicating them here is how two lists drift apart.
function OptionsEditor({ spec, filters, setFilters, ctx }) {
  const options = (ctx.optionsFor && ctx.optionsFor(spec.field)) || []
  const cur = filters[spec.field] || []
  const toggle = v => setFilters(f => {
    const s = new Set(f[spec.field] || [])
    s.has(v) ? s.delete(v) : s.add(v)
    return { ...f, [spec.field]: [...s] }
  })
  if (!options.length) return <span className="text-xs text-muted-foreground">{t('desk_kw_edit_no_options')}</span>
  return (
    <span className="flex flex-wrap items-center gap-1 max-w-[16rem]">
      {options.map(o => (
        <button key={o.value} type="button" onClick={() => toggle(o.value)}
          className={pill('none', { door: true, extra: cur.includes(o.value)
            ? 'border-primary bg-primary/15 text-foreground' : 'border-border text-muted-foreground hover:bg-muted' })}>
          {o.label}
        </button>
      ))}
    </span>
  )
}

// Districts are a LONG list, so this one filters. It edits the same districtCodes array the rail
// writes — picking here and picking there are the same act.
function DistrictsEditor({ filters, setFilters, ctx }) {
  const all = ctx.districtOptions || []
  const cur = filters.districtCodes || []
  // Adding a ✓ lifts the same code's ✕ (2026-10-22): one code in both arrays is an empty search.
  const toggle = code => setFilters(f => (f.districtCodes || []).includes(code)
    ? { ...f, districtCodes: f.districtCodes.filter(c => c !== code) }
    : addDistrictIncludes(f, 'districtCodes', [code]))
  const shown = all.filter(d => cur.includes(d.code) || !ctx.districtQuery
    || d.label.toLowerCase().includes(ctx.districtQuery.toLowerCase())).slice(0, 40)
  return (
    <span className="block max-w-[16rem]">
      <input value={ctx.districtQuery || ''} onChange={e => ctx.setDistrictQuery(e.target.value)}
        placeholder={t('desk_kw_edit_find_district')}
        className="w-full mb-1 rounded border border-border bg-background px-1.5 py-0.5 text-xs" />
      <span className="flex flex-wrap gap-1 max-h-40 overflow-y-auto">
        {shown.map(d => (
          <button key={d.code} type="button" onClick={() => toggle(d.code)}
            className={pill('none', { door: true, extra: cur.includes(d.code)
              ? 'border-primary bg-primary/15 text-foreground' : 'border-border text-muted-foreground hover:bg-muted' })}>
            {d.label}
          </button>
        ))}
      </span>
    </span>
  )
}

function FlagEditor({ spec, filters, setFilters }) {
  const on = !!filters[spec.field]
  return (
    <button type="button" onClick={() => setFilters(f => ({ ...f, [spec.field]: !on }))}
      className={pill('none', { door: true, extra: on
        ? 'border-primary bg-primary/15 text-foreground' : 'border-border text-muted-foreground hover:bg-muted' })}>
      {on ? t('desk_kw_edit_on') : t('desk_kw_edit_off')}
    </button>
  )
}

const SHAPES = { range: RangeEditor, set: SetEditor, options: OptionsEditor, districts: DistrictsEditor, flag: FlagEditor }

// Renders the editor for one chip. Unknown/absent editKey → null, and the caller simply doesn't
// offer editing for that keyword (identity keywords like a listing id or a phone are not
// "editable" in any meaningful sense — you remove them and type another).
export function KeywordEditor({ editKey, filters, setFilters, ctx = {} }) {
  const spec = KEYWORD_EDITORS[editKey]
  if (!spec) return null
  const Shape = SHAPES[spec.kind]
  return Shape ? <Shape spec={spec} filters={filters} setFilters={setFilters} ctx={ctx} /> : null
}

export const isEditableKeyword = editKey => !!KEYWORD_EDITORS[editKey]
